import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useUTM } from './UTMContext';

// Create context
const ConsentContext = createContext();

// Custom hook for using consent
export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
};




// Consent key for localStorage
const CONSENT_STORAGE_KEY = 'gtm_consent';

// Default consent state
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false
};

// Add at the top (after DEFAULT_CONSENT)
const GTM_ID = 'GTM-P5WG9H';

const loadGTM = () => {
  if (typeof window === 'undefined' || window.gtmLoaded) return;
  
  console.log('[GTM] Loading...');
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
  
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  script.onload = () => {
    window.gtmLoaded = true;
    console.log('[GTM] ✅ Loaded');
  };
  document.head.appendChild(script);
};

const unloadGTM = () => {
  if (typeof window === 'undefined') return;
  document.querySelectorAll('script[src*="googletagmanager.com/gtm.js"]').forEach(s => s.remove());
  window.gtmLoaded = false;
  console.log('[GTM] ❌ Unloaded');
};

// Add inside ConsentProvider component (after the useEffect that shows banner)

// Helper to check if analytics consent is granted
const hasAnalyticsConsent = () => {
  try {
    const consent = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || '{}');
    return consent.analytics === true;
  } catch {
    return false;
  }
};


// ============================================================
// Helper: Push pending utm_captured_consent event when consent is granted
// gclid is NEVER included in utm_captured event
// ============================================================
const pushPendingUtmCaptured = () => {
  if (typeof window === 'undefined') return;
  
  const pendingUtms = sessionStorage.getItem('pending_utm_captured');
  if (pendingUtms) {
    try {
      const utms = JSON.parse(pendingUtms);
      
      // ============================================================
      // gclid is NEVER included in utm_captured event
      // ============================================================
      const { gclid, ...utmsWithoutGclid } = utms;
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'utm_captured_consent',
        ...utmsWithoutGclid,
        'timestamp': new Date().toISOString(),
        'consent_granted': hasAnalyticsConsent()
      });
      sessionStorage.removeItem('pending_utm_captured');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[Consent] ✅ Pending utm_captured_consent pushed (gclid NEVER included)');
      }
    } catch (e) {
      console.error('[Consent] Failed to push pending utm_captured:', e);
    }
  }
};

// ============================================================
// Helper: Fire consent_granted event for GTM with UTMs
// gclid is NEVER included in consent_granted event
// ============================================================
const fireConsentEvent = (utmParams) => {
  if (typeof window === 'undefined') return;
  
  if (window.dataLayer) {
    // Filter out gclid - NEVER sent to GA4 via consent_granted
    const filteredUtms = {};
    Object.keys(utmParams).forEach(key => {
      if (key === 'gclid') return;
      if (utmParams[key] && utmParams[key] !== '(direct)' && utmParams[key] !== '(none)') {
        filteredUtms[key] = utmParams[key];
      }
    });
    
    // Include UTMs from sessionStorage (excluding gclid)
    const sessionUtms = {};
    const storageKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
    storageKeys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value && value !== '(direct)' && value !== '(none)') {
        sessionUtms[key] = value;
      }
    });
    
    const mergedUtms = { ...sessionUtms, ...filteredUtms };
    
    window.dataLayer.push({
      event: 'consent_granted',
      ...mergedUtms,
      timestamp: new Date().toISOString(),
      consent_granted: true
    });
    
    // ============================================================
    // Push pending utm_captured_consent event when consent is granted
    // ============================================================
    pushPendingUtmCaptured();
    
    // Fire custom event for any other listeners
    window.dispatchEvent(new Event('cookies_accepted'));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] Consent granted event fired (gclid NEVER included)');
    }
  }
};

// ============================================================
// Helper: Get UTMs for DB/CRM (includes gclid, no consent needed)
// UTMs are ALWAYS available in sessionStorage regardless of consent
// ============================================================
const getUTMsForCRM = () => {
  if (typeof sessionStorage === 'undefined') return {};
  
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'gclid'];
  const utms = {};
  
  keys.forEach(key => {
    const value = sessionStorage.getItem(key);
    if (value && value !== '' && value !== '(direct)' && value !== '(none)') {
      utms[key] = value;
    }
  });
  
  return utms;
};

// ============================================================
// Provider component
// ============================================================
export const ConsentProvider = ({ children }) => {
  const { utmParams, getUtmsForApi, getUtmsForDataLayer } = useUTM();
  
  const [consent, setConsent] = useState(() => {
    try {
      const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (savedConsent) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Consent] Loaded from localStorage:', JSON.parse(savedConsent));
        }
        return JSON.parse(savedConsent);
      }
    } catch (error) {
      console.error('[Consent] Error loading from localStorage:', error);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] Using default consent:', DEFAULT_CONSENT);
    }
    return DEFAULT_CONSENT;
  });

  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

 





 useEffect(() => {
  // Load GTM if either analytics OR marketing consent is granted
  if (consent.analytics || consent.marketing) {
    // loadGTM();
    
    // Move pending GCLID when analytics is granted
    if (consent.analytics) {
      const pendingGclid = sessionStorage.getItem('pending_gclid');
      if (pendingGclid) {
        sessionStorage.setItem('gclid', pendingGclid);
      }
      pushPendingUtmCaptured();
    }
  } else {
    // unloadGTM();
    sessionStorage.removeItem('gclid');
  }
}, [consent.analytics, consent.marketing]);



  // Show banner if no consent exists
  useEffect(() => {
    const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!savedConsent) {
      setIsBannerVisible(true);
    }
    
    // ============================================================
    // REMOVED: utm_captured_debug is now handled by GTM component
    // This prevents duplicate events and centralizes debug logic
    // ============================================================
    
  }, []); // Empty dependency array - only runs once on mount

  // ============================================================
  // LOAD PIPEDRIVE LEADBOOSTER CHAT
  // ============================================================
  const loadPipedrive = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (window.PipedriveLeadbooster) return; // Already loaded
    
    return new Promise((resolve) => {
      // Set config
      window.pipedriveLeadboosterConfig = {
        base: 'leadbooster-chat.pipedrive.com',
        companyId: 4204226,
        playbookUuid: '1d2238f6-980d-11e9-a398-fa163e66a870',
        version: 2
      };
      
      // Load script
      const script = document.createElement('script');
      script.src = 'https://leadbooster-chat.pipedrive.com/assets/loader.js';
      script.async = true;
      script.onload = () => {
        console.log('[Consent] ✅ Pipedrive LeadBooster Chat loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('[Consent] ❌ Failed to load Pipedrive Chat');
        resolve(); // Resolve anyway to not block other scripts
      };
      document.head.appendChild(script);
    });
  }, []);

  // ============================================================
  // LOAD MAILCHIMP (Frontend Tracking Script)
  // ============================================================
  const loadMailchimp = useCallback(async () => {
    if (typeof window === 'undefined') return;
    
    return new Promise((resolve) => {
      const userLang = navigator.language || navigator.userLanguage || 'en';
      let scriptUrl;
      
      if (userLang.toLowerCase().startsWith('fr')) {
        scriptUrl = "https://chimpstatic.com/mcjs-connected/js/users/fd07364dfadb6f746a4432f54/45fda49ca28cedbb713f497d8.js";
      } else {
        scriptUrl = "https://chimpstatic.com/mcjs-connected/js/users/fd07364dfadb6f746a4432f54/38c5ab4e05725c50b8ab48bde.js";
      }
      
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.id = 'mcjs';
      script.onload = () => {
        console.log('[Consent] ✅ Mailchimp loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('[Consent] ❌ Failed to load Mailchimp');
        resolve(); // Resolve anyway to not block other scripts
      };
      document.head.appendChild(script);
    });
  }, []);

  // ============================================================
  // LOAD FACEBOOK PIXEL
  // ============================================================
  const loadFacebookPixel = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (window.fbq) return; // Already loaded
    
    return new Promise((resolve) => {
      (function(f, b, e, v, n, t, s) {
        if (f.fbq) {
          resolve();
          return;
        }
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      
      // Initialize after script loads
      const checkFbq = setInterval(() => {
        if (window.fbq) {
          clearInterval(checkFbq);
          window.fbq('init', '128104927796644');
          window.fbq('track', 'PageView');
          console.log('[Consent] ✅ Facebook Pixel loaded and initialized');
          resolve();
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkFbq);
        console.warn('[Consent] ⚠️ Facebook Pixel load timeout');
        resolve();
      }, 5000);
    });
  }, []);

  // ============================================================
  // LOAD LINKEDIN INSIGHT TAG
  // ============================================================
  const loadLinkedInInsight = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (window.lintrk) return; // Already loaded
    
    return new Promise((resolve) => {
      window._linkedin_data_partner_id = "388778";
      const script = document.createElement('script');
      script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      script.async = true;
      script.onload = () => {
        if (window.lintrk) {
          window.lintrk('track');
        }
        console.log('[Consent] ✅ LinkedIn Insight loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('[Consent] ❌ Failed to load LinkedIn Insight');
        resolve();
      };
      document.head.appendChild(script);
    });
  }, []);

  // ============================================================
  // UNLOAD PIPEDRIVE CHAT
  // ============================================================
  const unloadPipedrive = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    // Remove config
    delete window.pipedriveLeadboosterConfig;
    delete window.PipedriveLeadbooster;
    
    // Remove script tags
    const scripts = document.querySelectorAll('script[src*="leadbooster-chat.pipedrive.com"]');
    scripts.forEach(script => script.remove());
    
    // Remove any chat containers/widgets
    const chatElements = document.querySelectorAll('[class*="pipedrive"], [id*="pipedrive"]');
    chatElements.forEach(el => {
      if (el.tagName !== 'SCRIPT') el.remove();
    });
    
    console.log('[Consent] ❌ Pipedrive Chat unloaded');
  }, []);

  // ============================================================
  // UNLOAD MAILCHIMP
  // ============================================================
  const unloadMailchimp = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    // Remove script tags
    const scripts = document.querySelectorAll('script[src*="chimpstatic.com"]');
    scripts.forEach(script => script.remove());
    
    // Remove mc.js related objects
    delete window.mc;
    delete window.MC;
    
    console.log('[Consent] ❌ Mailchimp unloaded');
  }, []);

  // ============================================================
  // UNLOAD FACEBOOK PIXEL
  // ============================================================
  const unloadFacebookPixel = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    // Remove script tags
    const scripts = document.querySelectorAll('script[src*="connect.facebook.net"]');
    scripts.forEach(script => script.remove());
    
    // Clear fbq
    window.fbq = null;
    delete window.fbq;
    delete window._fbq;
    
    console.log('[Consent] ❌ Facebook Pixel unloaded');
  }, []);

  // ============================================================
  // UNLOAD LINKEDIN INSIGHT
  // ============================================================
  const unloadLinkedInInsight = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    // Remove script tags
    const scripts = document.querySelectorAll('script[src*="snap.licdn.com"]');
    scripts.forEach(script => script.remove());
    
    // Clear LinkedIn
    window.lintrk = null;
    delete window.lintrk;
    delete window._linkedin_data_partner_id;
    
    console.log('[Consent] ❌ LinkedIn Insight unloaded');
  }, []);

  // ============================================================
  // MANAGE ALL MARKETING SCRIPTS - Load/Unload based on consent
  // ============================================================
  const manageThirdPartyScripts = useCallback(async (consentState) => {
    if (consentState.marketing) {
      // ✅ Load ALL marketing scripts
      console.log('[Consent] Loading all marketing scripts...');
      
      await Promise.all([
        loadPipedrive(),
        loadMailchimp(),
        loadFacebookPixel(),
        loadLinkedInInsight()
      ]);
      
      console.log('[Consent] ✅ All marketing scripts loaded');
    } else {
      // ❌ Unload ALL marketing scripts
      console.log('[Consent] Unloading all marketing scripts...');
      
      unloadPipedrive();
      unloadMailchimp();
      unloadFacebookPixel();
      unloadLinkedInInsight();
      
      console.log('[Consent] ❌ All marketing scripts unloaded');
    }
  }, [loadPipedrive, loadMailchimp, loadFacebookPixel, loadLinkedInInsight, unloadPipedrive, unloadMailchimp, unloadFacebookPixel, unloadLinkedInInsight]);

  // ============================================================
  // Update GTM when consent changes
  // ============================================================
  useEffect(() => {
    const updateGTMConsent = () => {
      try {
        if (window.gtag) {
          window.gtag('consent', 'update', {
            'ad_storage': consent.marketing ? 'granted' : 'denied',
            'analytics_storage': consent.analytics ? 'granted' : 'denied',
            'ad_user_data': consent.marketing ? 'granted' : 'denied',
            'ad_personalization': consent.marketing ? 'granted' : 'denied',
            'functionality_storage': consent.preferences ? 'granted' : 'denied',
            'personalization_storage': consent.marketing ? 'granted' : 'denied',
            'security_storage': 'granted'
          });
        }

        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'consent_update',
            'consent': consent
          });
        }
      } catch (error) {
        console.error('[Consent] Error updating GTM consent:', error);
      }
    };

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
      if (process.env.NODE_ENV === 'development') {
        console.log('[Consent] Saved to localStorage:', consent);
      }
    } catch (error) {
      console.error('[Consent] Error saving to localStorage:', error);
    }
    
    updateGTMConsent();
    
    // 🔥 Manage third-party scripts based on consent
    manageThirdPartyScripts(consent);
    
    // ============================================================
    // Fire consent_granted event with UTMs when analytics consent is granted
    // gclid is NEVER included in consent_granted event
    // ============================================================
    if (consent.analytics) {
      const utmsForEvent = getUtmsForDataLayer();
      if (utmsForEvent && Object.keys(utmsForEvent).length > 0) {
        fireConsentEvent(utmsForEvent);
      }
    }
    
    // ============================================================
    // REMOVED: utm_captured_debug is now handled by GTM component
    // This prevents duplicate events and centralizes debug logic
    // ============================================================
    
  }, [consent, manageThirdPartyScripts, utmParams, getUtmsForDataLayer]);

  // ============================================================
  // acceptAll - NO PAGE RELOAD
  // ✅ UTMs ALWAYS available in sessionStorage
  // ✅ GCLID stored with consent
  // ✅ DataLayer event fired for GTM
  // ============================================================
  const acceptAll = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] Accepting all cookies - NO PAGE RELOAD');
    }
    
    const newConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    setConsent(newConsent);
    setIsBannerVisible(false);
    
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
    } catch (error) {
      console.error('[Consent] Error saving to localStorage:', error);
    }

    // ============================================================
    // 🔥 FIRE DATA LAYER EVENT INSTEAD OF PAGE RELOAD
    // This tells GTM to push UTMs to GA4
    // ============================================================
    if (window.dataLayer) {
      // Get UTMs from sessionStorage (already stored)
      const utms = {
        utm_source: sessionStorage.getItem('utm_source'),
        utm_medium: sessionStorage.getItem('utm_medium'),
        utm_campaign: sessionStorage.getItem('utm_campaign'),
        utm_content: sessionStorage.getItem('utm_content'),
        utm_term: sessionStorage.getItem('utm_term'),
        gclid: sessionStorage.getItem('gclid')
      };
      
      // Push consent_granted event with UTMs
      window.dataLayer.push({
        event: 'consent_granted',
        ...utms,
        timestamp: new Date().toISOString(),
        consent_type: 'all'
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[Consent] ✅ consent_granted event fired (NO page reload)');
        console.log('[Consent] UTMs in sessionStorage:', utms);
      }
    }
    
    // ============================================================
    // Store GCLID in sessionStorage when consent is granted
    // ============================================================
    try {
      const params = new URLSearchParams(window.location.search);
      const gclidValue = params.get('gclid');
      
      if (gclidValue && gclidValue !== '') {
        sessionStorage.setItem('gclid', gclidValue);
        if (process.env.NODE_ENV === 'development') {
          console.log('[Consent] ✅ GCLID stored in sessionStorage:', gclidValue);
        }
      } else {
        const existingGclid = sessionStorage.getItem('gclid');
        if (existingGclid) {
          console.log('[Consent] ✅ GCLID already in sessionStorage:', existingGclid);
        } else {
          console.log('[Consent] ℹ️ No GCLID found in URL or sessionStorage');
        }
      }
    } catch (e) {
      console.error('[Consent] Error storing GCLID:', e);
    }
    
    // Update GTM consent
    try {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'analytics_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'functionality_storage': 'granted',
          'personalization_storage': 'granted',
          'security_storage': 'granted'
        });
      }
    } catch (error) {
      console.error('[Consent] Error updating GTM consent:', error);
    }
    
    // Push pending UTMs
    pushPendingUtmCaptured();
    
    // Fire custom event for any other listeners
    window.dispatchEvent(new Event('cookies_accepted'));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] ✅ Consent accepted - NO PAGE RELOAD');
    }
  };

  // ============================================================
  // rejectAll - NO PAGE RELOAD
  // ✅ UTMs ALWAYS remain in sessionStorage for DB/CRM
  // ✅ GCLID removed
  // ============================================================
  const rejectAll = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] Rejecting all non-necessary cookies - NO PAGE RELOAD');
    }
    
    const newConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    setConsent(newConsent);
    setIsBannerVisible(false);
    
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
    } catch (error) {
      console.error('[Consent] Error saving to localStorage:', error);
    }
    
    // ============================================================
    // 🔥 FIRE REJECT EVENT (NO page reload)
    // UTMs stay in sessionStorage for DB/CRM use
    // ============================================================
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_rejected',
        timestamp: new Date().toISOString(),
        consent_type: 'necessary_only'
      });
    }
    
    // ============================================================
    // Remove GCLID from sessionStorage when consent is denied
    // ============================================================
    try {
      sessionStorage.removeItem('gclid');
      if (process.env.NODE_ENV === 'development') {
        console.log('[Consent] ❌ GCLID removed from sessionStorage');
      }
    } catch (e) {
      console.error('[Consent] Error removing GCLID:', e);
    }
    
    // Update GTM consent
    try {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'analytics_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'functionality_storage': 'denied',
          'personalization_storage': 'denied',
          'security_storage': 'granted'
        });
      }
    } catch (error) {
      console.error('[Consent] Error updating GTM consent:', error);
    }
    
    // Fire custom event for any other listeners
    window.dispatchEvent(new Event('cookies_rejected'));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] ✅ Consent rejected - NO PAGE RELOAD');
    }
  };

  // ============================================================
  // acceptSelected - NO PAGE RELOAD
  // ============================================================
  const acceptSelected = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] Accepting selected preferences - NO PAGE RELOAD:', consent);
    }
    
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
      setIsBannerVisible(false);
      
      // ============================================================
      // 🔥 FIRE DATA LAYER EVENT INSTEAD OF PAGE RELOAD
      // ============================================================
      if (window.dataLayer) {
        const utms = {
          utm_source: sessionStorage.getItem('utm_source'),
          utm_medium: sessionStorage.getItem('utm_medium'),
          utm_campaign: sessionStorage.getItem('utm_campaign'),
          utm_content: sessionStorage.getItem('utm_content'),
          utm_term: sessionStorage.getItem('utm_term'),
          gclid: sessionStorage.getItem('gclid')
        };
        
        window.dataLayer.push({
          event: 'consent_granted',
          ...utms,
          timestamp: new Date().toISOString(),
          consent_type: 'selected'
        });
      }
      
      // Update GTM consent
      try {
        if (window.gtag) {
          window.gtag('consent', 'update', {
            'ad_storage': consent.marketing ? 'granted' : 'denied',
            'analytics_storage': consent.analytics ? 'granted' : 'denied',
            'ad_user_data': consent.marketing ? 'granted' : 'denied',
            'ad_personalization': consent.marketing ? 'granted' : 'denied',
            'functionality_storage': consent.preferences ? 'granted' : 'denied',
            'personalization_storage': consent.marketing ? 'granted' : 'denied',
            'security_storage': 'granted'
          });
        }
      } catch (error) {
        console.error('[Consent] Error updating GTM consent:', error);
      }
      
      // ============================================================
      // Store GCLID when analytics consent is granted
      // ============================================================
      if (consent.analytics) {
        try {
          const params = new URLSearchParams(window.location.search);
          const gclidValue = params.get('gclid');
          
          if (gclidValue && gclidValue !== '') {
            sessionStorage.setItem('gclid', gclidValue);
            if (process.env.NODE_ENV === 'development') {
              console.log('[Consent] ✅ GCLID stored in sessionStorage:', gclidValue);
            }
          } else {
            const existingGclid = sessionStorage.getItem('gclid');
            if (existingGclid) {
              console.log('[Consent] ✅ GCLID already in sessionStorage:', existingGclid);
            }
          }
        } catch (e) {
          console.error('[Consent] Error storing GCLID:', e);
        }
      } else {
        // Remove GCLID if analytics consent is denied
        try {
          sessionStorage.removeItem('gclid');
          if (process.env.NODE_ENV === 'development') {
            console.log('[Consent] ❌ GCLID removed (analytics consent denied)');
          }
        } catch (e) {
          console.error('[Consent] Error removing GCLID:', e);
        }
      }
      
      if (consent.analytics) {
        pushPendingUtmCaptured();
      }
      
      window.dispatchEvent(new Event('cookies_accepted'));
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[Consent] ✅ Selected preferences accepted - NO PAGE RELOAD');
      }
    } catch (error) {
      console.error('[Consent] Error saving selected consent:', error);
    }
  };

  // ============================================================
  // resetConsent - NO PAGE RELOAD
  // ============================================================
  const resetConsent = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] Resetting consent - NO PAGE RELOAD');
    }
    
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch (error) {
      console.error('[Consent] Error removing from localStorage:', error);
    }
    
    setConsent(DEFAULT_CONSENT);
    setIsBannerVisible(true);
    setShowPreferences(false);
    
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_reset',
        timestamp: new Date().toISOString()
      });
    }
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'analytics_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'functionality_storage': 'denied',
        'personalization_storage': 'denied',
        'security_storage': 'granted'
      });
    }
    
    // Remove GCLID when consent is reset to default (denied)
    try {
      sessionStorage.removeItem('gclid');
    } catch (e) {}
    
    window.dispatchEvent(new Event('cookies_reset'));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Consent] ✅ Consent reset - NO PAGE RELOAD');
    }
  };

  const updateConsent = (newConsent) => {
    setConsent(prev => ({ ...prev, ...newConsent }));
  };

  const hasConsent = (type) => {
    return consent[type] === true;
  };

  const getUTMsForAPI = useCallback(() => {
    const includeGclid = consent.analytics === true;
    return getUtmsForApi(includeGclid);
  }, [getUtmsForApi, consent.analytics]);

  const getUTMsForDataLayer = useCallback(() => {
    return getUtmsForDataLayer();
  }, [getUtmsForDataLayer]);

  const getUTMsForCRMValue = useCallback(() => {
    return getUTMsForCRM();
  }, []);

  return (
    <ConsentContext.Provider
      value={{
        consent,
        updateConsent,
        acceptAll,
        rejectAll,
        acceptSelected,
        resetConsent,
        isBannerVisible,
        setIsBannerVisible,
        showPreferences,
        setShowPreferences,
        hasConsent,
        getUTMsForAPI,
        getUTMsForDataLayer,
        getUTMsForCRM: getUTMsForCRMValue
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export { getUTMsForCRM };